import { html, type TemplateResult } from 'lit';
import Navigo, { type AfterHook, type BeforeHook, type Match, type NavigateOptions } from 'navigo';
import { I18n } from '../../../../_ui-core_/base/package/services/package/i18n/i18n.de';
import { getNativeAppInfo } from '../../../../_ui-core_/base/package/util/environment';
import { dispatchCustomEvent } from '../../../../_ui-core_/base/package/util/event';
import { addMetaPreloadLink, resetRobotsTagToDefault } from '../../../../_ui-core_/base/package/util/html-helper';
import { paintTick, sleep, tick } from '../../../../_ui-core_/base/package/util/render';
import { SEMVER } from '../../../../_ui-core_/base/package/util/semver';
import { sessionStorage_getOrNull } from '../../../../_ui-core_/base/package/util/sessionStorage';
import { sanitizePathSegment } from '../../../../_ui-core_/base/package/util/url';
import type { RenderRootCallbacks } from '../../../../app-root';
import { eventBus } from '../../../../eventBus/eventBus';
import { ROUTER_EVENTS } from '../../../../eventBus/registry/router.events';
import { BasePageHeaderType } from '../../../../page-templates/base-page/base-page-header';
import '../../../../page-templates/cat-filter-page/cat-filter-page';
import '../../../../pages/category/category-page';
import '../../../../pages/club-thousands/club-thousands-page';
import '../../../../pages/errors/error-page-404';
import '../../../../pages/game/game-info-page';
import '../../../../pages/game/game-page';
import '../../../../pages/help-center/help-center-page';
import '../../../../pages/lobby-sh/lobby-page-sh';
import '../../../../pages/lobby/lobby-page';
import '../../../../pages/login-required/login-required-page';
import { LogOutReason } from '../../../../pages/logout/page';
import '../../../../pages/maintenance/maintenance-page';
import '../../../../pages/providers/providers-list-page';
import '../../../../pages/rtp/page';
import '../../../../pages/search/search-page';
import '../../../../pages/sitemap/sitemap-page';
import { TileType } from '../../../../_ui-core_/base/package/api/package/gaming/gaming-domain';
import { SESSION_STORAGE_KEY_DISABLE_MULTITAB } from '../../../../constants';
import type Application from '../Application';
import { ComplianceActionTypes, UserStates } from '../service/activation-flow/activation-flow-domain';
import { MultiTabService } from '../service/browser/multitab-service';
import { LoginStatus } from '../service/login/login-domain';
import { Product } from '../service/product/product-domain';
import { Layout, type RouteOptions } from '../types';
import { MainRoute } from './router-types/main-route';
import { type BaseTemplate, type PageTemplate, TemplateType } from './router-types/page-template-types';
import { type RouteGuard, type RouteGuardOutcome, RouteGuardOutcomeType } from './router-types/route-guard-types';
import { TabNavigationType } from './router-types/router-types';
import {
  type ClearSidebarEvent,
  type ShowSidebarEvent,
  SidebarEventName,
  SidebarEventType,
} from './router-types/sidebar-types';
import '../../../../_ui-core_/components/ui-spinner/ui-spinner';
import '../../../../page-templates/blank-page/blank-page';

/**
 * How the login-required page is framed. Shared by the dedicated
 * `LOGIN_REQUIRED` route and the auth guard's BLANK fallback in `renderView()`,
 * so both render the same screen.
 */
const LOGIN_REQUIRED_PAGE_TEMPLATE: BaseTemplate = {
  template: TemplateType.BASE,
  options: {
    headerType: BasePageHeaderType.FULL,
    isFooterShown: false,
  },
};

type ShortCutLink = {
  key: string;
  path: string | null;
  action: (() => void) | null;
};

export { MainRoute };

export default class Router {
  private _currentLocation = '/';
  private _gamePageOpen = false;
  private _hasCompletedFirstRoute = false;
  private _isSidebarOpen = false;
  private _loggedIn: boolean | undefined = undefined;
  private _multitabServivce: MultiTabService | undefined = undefined;
  /** Bumped per navigation, so a route guard that resolves after the user moved on is dropped. */
  private _navigationId = 0;
  private _preSlideInPath: string | undefined = undefined;
  private _previousLocation: string | null = null;
  private _root: RenderRootCallbacks;
  // The <base-page> root last rendered by _renderViewAsPage. Only <base-page>
  // renders desktop slide-ins (IGM-482).
  private _basePageRoot: TemplateResult | null = null;
  private _rootPath = '/';
  private _router: Navigo;
  private _routesFinalized = false;
  private _scrollToTopScheduledId = 0;

  private readonly _app: Application;
  private readonly _isDesktop: boolean;
  private readonly _scrollPostions = new Map<string, number>();
  private readonly _showWidgetFrameTimeout?: ReturnType<typeof setTimeout>;
  private readonly _tipicoGamesBlogUrl = 'https://games.tipico.de/slots';

  // Routes that act as forwarders (redirect to external pages) and should never
  // create a history entry. The beforeHook replaces the history entry with the
  // previous location so back-navigation skips over them.
  private readonly _forwarderRoutes = new Set(['login', 'sign-up', 'sports', 'sports/:page']);

  // Example shortcut link <a href="#linkTo_terms-and-conditions">Terms</a>
  private readonly _shortcutPrefix = '#linkTo_';
  private readonly _shortcuts: ShortCutLink[] = [
    { key: 'login', path: null, action: () => window.$app.product.gotoLogin('shortcut') },
    { key: 'privacy-policy', path: '/privacy-policy', action: null },
    { key: 'terms-and-conditions', path: '/terms-and-conditions', action: null },
  ];

  private readonly _afterHook: AfterHook = (match) => {
    window.$app.track.pageVisited(match.url || 'home');
    window.$app.routerStore.next(match, false);
    this._currentLocation = match.url;
    this._hasCompletedFirstRoute = true;

    // Multitab handling. Acquire lock once when logged in.
    this._loginStatus().then((loginStatus) => {
      if (loginStatus === LoginStatus.LOGGED_IN) {
        // Dev: skip multi-tab lock when disabled via sessionStorage
        if (sessionStorage_getOrNull(SESSION_STORAGE_KEY_DISABLE_MULTITAB)) return;
        // Native apps cannot have more than 1 active tab
        if (window.$app.device.isNativeApp) return;
        if (this._multitabServivce === undefined) {
          this._multitabServivce = new MultiTabService();
          this._multitabServivce.init(() => this._lockTab());
          this._multitabServivce.activateTab();
        }
      }
    });

    if (this._loggedIn) {
      window.$app.vaix.trackPageImpressionVaix(this._currentLocation, this._previousLocation);
    }
  };

  private readonly _beforeHook: BeforeHook = (done, match) => {
    this._navigationId++;
    clearTimeout(this._showWidgetFrameTimeout);
    resetRobotsTagToDefault();
    this._removeTrailingSlash();
    this._gamePageOpen = false;

    // WIP, disallowed route checking, disabled until product decides what to do: IGM-356
    // const routeIsAllowed = this._app.accountStatusService.isRouteAllowed(match.route.name.split('/')[1]);
    // if (!routeIsAllowed) {
    //   return;
    // }

    this._previousLocation = this._currentLocation;
    window.$app.activationFlowModalService.closeFinishedTimers();

    if (this._isForwarderRoute(match.route.name)) {
      if (this._handleForwarderRoute(done as (shouldContinue: false | Match) => void)) return;
    }

    if (this._isDesktop) {
      this._clearDesktopSlideIn();
    }

    // Undo previous scroll restoration
    this._cleanupScrollRestoration();
    // TODO: don't scroll to top when slide-in was opened or closed
    this._scrollToTopScheduledId = requestAnimationFrame(() => {
      tick().then(() => this.scrollToTop());
    });

    // Prepare scroll restoration
    this._storeScrollPosition(this._previousLocation);
    done(match);
  };

  constructor(app: Application, root: string = MainRoute.HOME) {
    window.$app.logger.log(`construct router with root path: '${root}'`);
    this._app = app;
    this._isDesktop = this._app.layoutStore.value === Layout.Desktop;
    this._rootPath = root;
    this._router = new Navigo(root);
    this._subscribeLoginStatus();

    this._setupEventBusListeners();
  }

  public isGamePageOpen() {
    return this._gamePageOpen;
  }

  public currentLocation(): Match {
    const location = this._router.getCurrentLocation?.();
    if (!location) {
      window.$app.logger.warn('router/getCurrentLocation is undefined');
      return {
        url: '',
        queryString: '',
        hashString: '',
        route: { name: '', path: '', handler: () => {}, hooks: {} },
        data: null,
        params: null,
      };
    }
    return location;
  }

  public async restoreScrollPosition(id = this._currentLocation) {
    cancelAnimationFrame(this._scrollToTopScheduledId);
    const pos = this._scrollPostions.get(id) ?? 0;

    // Scroll to the previous position if it's not the same as the current position.
    if (window.scrollY !== pos) {
      const taskId = window.$app.renderService.addTask('restore-scroll-position');
      await paintTick();
      const minBodyHeight = pos + window.innerHeight;
      document.body.style.minHeight = `${minBodyHeight}px`;
      window.scrollTo({ top: pos });
      await paintTick();
      window.$app.renderService.removeTask(taskId);
    }
  }

  public initRouter(root: RenderRootCallbacks): void {
    window.$app.logger.log('initialize router');
    this._getSourceAndCleanUrlForTracking();
    this._root = root;
    // All instances of the app require either no scrolling or a reset to 0 on arrival.
    // Setting scrollRestoration to manual avoids a scroll flicker when navigating back on cat-filter-page.
    history.scrollRestoration = 'manual';
    this._addNotFound();
    this._initHooks();

    this._router
      .on(MainRoute.MAINTENANCE, () => {
        this._root.set(html`<maintenance-page></maintenance-page>`);
      })
      .on(MainRoute.SEARCH, () => {
        this._renderSearchPage();
      })
      .on(MainRoute.PROVIDER, () => {
        this.renderView(html`<providers-list-page></providers-list-page>`, {
          template: TemplateType.BASE,
          options: {
            headerType: BasePageHeaderType.WITH_ICONS,
          },
        });
      })
      .on(`${MainRoute.PROVIDER}/:slug`, (match) => {
        if (!match?.data?.slug) {
          // missing data => not found
          this._notFoundHandler();
          return;
        }
        const slug = sanitizePathSegment(match.data.slug);
        if (!slug) {
          this._notFoundHandler();
          return;
        }
        const lobbyStore = window.$app.content.getLobby();
        lobbyStore.subscribeOnce((lobby) => {
          // Find studio with slug
          const studio = lobby!.studios.find((s) => s.slug === slug);
          if (studio === undefined) {
            // Studio not found
            window.$app.logger.warn(`slug '${slug}' not found`);
            this._notFoundHandler();
            return;
          }
          const categoryId = studio.filterId;
          const categoryName = studio.name;
          window.$app.logger.log(`slug '${slug}' matches studio '${categoryName}'`);
          this.renderView(
            html`
              <cat-filter-page>
                <category-page .categoryId=${categoryId} .personalizationCategorySlug=${slug} .categoryName=${categoryName}></category-page>
              </cat-filter-page>
            `,
            {
              template: TemplateType.BASE,
              options: {
                headerType: BasePageHeaderType.WITH_ICONS,
              },
            },
          );
        }, true);
      })
      .on(
        `/${MainRoute.GAME}/:gameMode/:gameId`,
        async (match) => {
          if (match?.data?.gameId === undefined) {
            // missing data => not found
            this._notFoundHandler();
            return;
          }

          // Sanitize gameId to prevent directory traversal (TIP-08-004)
          const gameId = sanitizePathSegment(match.data.gameId);
          if (!gameId) {
            this._notFoundHandler();
            return;
          }

          // Add canonical URL to head (SEO)
          const gameDetails = await window.$app.content.getGameInfo(gameId).catch((err) => {
            window.$app.logger.warn(`Error fetching game info '${gameId}'`, err);
          });

          const slug = gameDetails?.slug || '';
          const studio = gameDetails?.getStudio();

          // When logged-out, redirect to game info page.
          if (this._loggedIn === undefined) {
            await this._loginStatus();
          }
          if (this._loggedIn === false) {
            this.navigateToGameInfo(slug);
            return;
          }

          this._gamePageOpen = true;

          const isReturningFromSidebar = this._preSlideInPath !== undefined;

          if (!isReturningFromSidebar) {
            // Free decoded images from GPU memory before loading the game (IGM-3644).
            // Lobby tiles/banners stay decoded in the GPU process even after DOM removal.
            // Clearing src forces WebKit to release the image buffers.
            this._releaseImageMemory();

            // Clear the current page DOM and pause to give WebKit time to GC the
            // previous page's element tree before the game iframe allocates heavy
            // resources (WebGL, audio, workers). Without this, the lobby's DOM
            // coexists with the game iframe under memory pressure, causing iOS
            // process termination on older devices (IGM-3644).
            this._root.set(html`<div style="position:fixed;inset:0;background:var(--ui-color-base)"></div>`);
            await sleep(100);
          }

          // Desktop page
          if (this._isDesktop) {
            const pageTemplate = {
              template: TemplateType.BASE,
              options: {
                allowDesktopSlideIn: false,
                headerType: BasePageHeaderType.HIDDEN,
                isFooterShown: false,
                isFullScreen: true,
              },
            };

            this.renderView(
              html`<game-page
                .layout=${Layout.Desktop}
                .forReal="${match.data.gameMode !== 'demo'}"
                .gameId="${gameId}"
                .gameSlug=${slug}
                .studio=${studio}
              ></game-page>`,
              pageTemplate,
            );
            return;
          }

          // Mobile page
          this._root.set(
            html`<game-page
              .layout=${Layout.Mobile}
              .forReal="${match.data.gameMode !== 'demo'}"
              .gameId="${gameId}"
              .gameSlug=${slug}
              .studio=${studio}
            ></game-page>`,
          );
        },
        {
          leave: (done) => {
            window.$app.logger.log('leave hook: refresh balance and last played games…');
            window.$app.refreshBalance();
            window.$app.content.refreshCategory('userLastPlayed');
            done();
          },
        },
      )
      .on(MainRoute.GAMES_INFO, () => {
        this.renderView(html`<rtp-page .headerTitle=${this.getBackButtonLabel()}></rtp-page>`, {
          template: TemplateType.BASE,
          options: {
            headerType: BasePageHeaderType.WITH_ICONS,
          },
        });
      })
      .on('portal/:page', (match) => {
        const page = match?.data?.page ?? 'home';
        window.$app.product.gotoProduct(Product.PORTAL, page);
      })
      .on('login', () => {
        // Used in banner CTA for instance
        window.$app.product.gotoLogin('deeplink');
      })
      .on('sign-up', () => {
        // Used in banner CTA for instance
        window.$app.logger.log('Navigating to sign-up page');
        window.$app.product.gotoSignup(window.location.href);
      })
      .on('logout', () => {
        this._root.set(html`<logout-page></logout-page>`);
      })
      .on('logout/:reason', (match) => {
        this._root.set(html`<logout-page .reason=${match?.data?.reason as LogOutReason}></logout-page>`);
      })
      .on('sports', () => {
        window.$app.product.gotoProduct(Product.SPORTS, '');
      })
      .on('sports/:page', (match) => {
        const page = match?.data?.page ?? 'home';
        window.$app.product.gotoProduct(Product.SPORTS, page);
      })
      .on(`${MainRoute.TAG}/:slug`, (match) => {
        if (!match?.data?.slug) {
          // missing data => not found
          this._notFoundHandler();
          return;
        }
        const slug = sanitizePathSegment(match.data.slug);
        if (!slug) {
          this._notFoundHandler();
          return;
        }
        const lobbyStore = window.$app.content.getLobby();
        lobbyStore.subscribeOnce((lobby) => {
          // Find tag with slug
          const tag = lobby!.tags.find((t) => t.slug === slug);
          if (tag === undefined) {
            // Tag not found
            window.$app.logger.warn(`slug '${slug}' not found`);
            this._notFoundHandler();
            return;
          }
          window.$app.logger.log(`slug '${slug}' matches tag '${tag.name}'`);
          this.renderView(
            html`
              <cat-filter-page>
                <category-page .tagSlug=${tag.slug} .tagName=${tag.name} .personalizationCategorySlug=${slug}></category-page>
              </cat-filter-page>
            `,
            {
              template: TemplateType.BASE,
              options: {
                headerType: BasePageHeaderType.WITH_ICONS,
              },
            },
          );
        }, true);
      })
      .on(`${MainRoute.CATEGORY}/:slug`, (match) => {
        if (!match?.data?.slug) {
          // missing data => not found
          this._notFoundHandler();
          return;
        }
        const slug = sanitizePathSegment(match.data.slug);
        if (!slug) {
          this._notFoundHandler();
          return;
        }
        const lobbyStore = window.$app.content.getLobby();
        lobbyStore.subscribeOnce((lobby) => {
          // Find category with slug
          let category = lobby!.categories.find((cat) => cat.slug === slug);
          if (category === undefined) {
            category = lobby!.searchCategories.find((cat) => cat.slug === slug);
          }
          if (category === undefined) {
            // Category not found
            window.$app.logger.warn(`slug '${slug}' not found => show not found`);
            this._notFoundHandler();
            return;
          }
          const { filterId, title } = category;
          window.$app.logger.log(`slug '${slug}' matches category '${title}'`);
          this.renderView(
            html`
            <cat-filter-page>
              <category-page .categoryId=${filterId} .personalizationCategorySlug=${slug} .categoryName=${title}></category-page>
            </cat-filter-page>
          `,
            {
              template: TemplateType.BASE,
              options: {
                headerType: BasePageHeaderType.WITH_ICONS,
              },
            },
          );
        }, true);
      })
      .on(MainRoute.SITEMAP, () => {
        this.renderView(html`<sitemap-page .headerTitle=${this.getBackButtonLabel()}></sitemap-page>`, {
          template: TemplateType.BASE,
          options: {
            headerType: BasePageHeaderType.WITH_ICONS,
            isFooterShown: true,
          },
        });
      })
      .on(MainRoute.LOGIN_REQUIRED, async () => {
        // Replace, not push, so back does not return here. Navigo queues a
        // navigate issued mid-resolve until its own pushState has run, so the
        // replace overwrites this entry.
        if ((await this._loginStatus()) === LoginStatus.LOGGED_IN) {
          window.$app.logger.log('login-required visited while logged in => replace with home');
          this.navigateReplace(this.getPathNavigateToHome(false));
          return;
        }
        this.renderView(html`<login-required-page></login-required-page>`, LOGIN_REQUIRED_PAGE_TEMPLATE);
      })
      .on('/', () => this._renderLobby());
  }

  public hasRoute(path: string): boolean {
    return this._router.getRoute(path) !== undefined;
  }

  /**
   * @param routeOptions.guard - resolved before `handler`; deny and redirect leave the route with
   * replace semantics, so back does not return to it
   */
  public addRoute(path: string, handler: (match?: Match) => void, routeOptions?: RouteOptions): void {
    if (routeOptions?.replaceExisting === true && this.hasRoute(path)) {
      window.$app.logger.log(`addRoute('${path}') replacing existing route`);
      // Remove route
      this._router.off(path);
      // Remove route with root path
      const rootPath = path.startsWith('/') ? `${this._rootPath}${path}` : `${this._rootPath}/${path}`;
      this._router.off(rootPath);
    } else if (this.hasRoute(path)) {
      window.$app.logger.log(`addRoute('${path}') canceled because route already exists`);
      return;
    }
    window.$app.logger.log(`addRoute('${path}')`);
    const guard = routeOptions?.guard;
    this._router.on(path, (match) => {
      if (guard === undefined) {
        handler(match);
        return;
      }
      void this._resolveGuardedRoute(guard, match, handler);
    });
  }

  /**
   * Register the game info route using the dynamic slug from featureFlag.
   * Must be called after featureFlag is initialized.
   */
  public registerGameInfoRoute(): void {
    this.addRoute(`/${this._gameInfoSlug}/:slug`, (match) => {
      if (!match?.data?.slug) {
        this._notFoundHandler();
        return;
      }
      const slug = sanitizePathSegment(match.data.slug);
      if (!slug) {
        this._notFoundHandler();
        return;
      }

      // Prefetch game details at route-match time. The cache deduplicates,
      // so the page's subsequent getGameInfo call gets the same in-flight promise.
      // Also start media preload as soon as the response arrives.
      window.$app.content
        .getGameInfo(undefined, slug, TileType.L1)
        .then((game) => {
          if (!game.vid && game.img) {
            addMetaPreloadLink(game.img, 'image', true);
          }
        })
        .catch(() => {
          // Errors handled by the page component
        });

      this._renderGameInfoPage(slug);
    });
  }

  /**
   * Start handling main routes and show spinner for unknown routers until fully setup.
   * Module routes will be added later!
   */
  public startRouter() {
    window.$app.perf.setTag('routerStart');
    window.$app.logger.log('start resolving routes');
    if (!this._isDesktop) {
      // Only resolve router on mobile here to avoid sidebar issues on desktop
      this._router.resolve();
    }
  }

  /**
   * Called after all routes have been added.
   */
  public routesFinalized() {
    this._routesFinalized = true;
    window.$app.perf.setTag('routerFinalized');
    window.$app.logger.log('routes finalized');

    // Check if the current path matches any route
    const match = this._router.match(window.location.pathname);
    if (match) {
      // Resolve it to trigger the specific route handler
      this._router.resolve();
    } else {
      // Still no route found => show 404 error page
      this._notFoundHandler();
    }
  }

  /**
   * A valid shortcut starts with the shortcut prefix or the current domain + shortcut prefix.
   *
   * @param href - The link to check.
   * @returns True if the link is a valid shortcut, false otherwise.
   */
  public isShortcutLink(href?: string): boolean {
    if (!href) {
      return false;
    }
    const currentPathWithPrefix = `${document.location.href}${this._shortcutPrefix}`;
    return href.startsWith(this._shortcutPrefix) || href.startsWith(currentPathWithPrefix);
  }

  public navigateBack(): void {
    if (this._previousLocation === undefined) {
      // No previous location => navigate to home
      this.navigateToHome();
      return;
    }

    if (this._previousLocation && ['/', ''].includes(this._previousLocation)) {
      this.navigateToHome();
      return;
    }

    history.back();
  }

  /**
   * Link types:
   * - link starts with a `/` => page
   * - link starts without a `/` => bespoke component
   * - link starts with http(s) => external link
   */
  public navigateTo(
    page: string,
    tabBehavior: TabNavigationType = TabNavigationType.INTERNAL_SAME_EXTERNAL_NEW,
    complianceAction?: ComplianceActionTypes,
  ): void {
    // Force tipico games blog to open in a new tab. Even if it is on the same origin.
    // Games prod: https://games.tipico.de
    // Games blog: https://games.tipico.de/slots
    if (page.startsWith(this._tipicoGamesBlogUrl)) {
      // biome-ignore lint/style/noParameterAssign: no-unsafe-assignment
      tabBehavior = TabNavigationType.NEW;
    }

    const isCompliant = !complianceAction || window.$app.activationFlow.isActionEnabled(complianceAction);

    if (!isCompliant) {
      window.$app.activationFlowModalService.toggleAllMinification(true);
      window.$app.activationFlowModalService.requestUiUpdate();
      window.$app.logger.log(`navigate to '${page}' was cancelled due to compliance reasons`);
      return;
    }

    window.$app.logger.log(`navigate to '${page}', tabBehavior '${tabBehavior}'`);
    let isInternal = true;
    let isAbsolute = false;
    if (page.startsWith('https://') || page.startsWith('http://')) {
      isAbsolute = true;
      isInternal = new URL(page).origin === document.location.origin;
    }

    const navigateInternalToSameTab = () => {
      if (isAbsolute) {
        window.$app.logger.log(`⚠️ Doing in-app navigation via absolute links is discouraged! Page: ${page}`);
        window.open(page, '_self');
      } else {
        this._router.navigate(page);
      }
    };

    const navigateInternalToNewTab = () => {
      const appVersion = getNativeAppInfo()?.nativeAppVersion ?? '0';
      const isFixedVersion = SEMVER.compare(appVersion, '6.0.1', '>=');
      if (window.$app.device.isNativeAppIos || (window.$app.device.isNativeAppAndroid && isFixedVersion)) {
        this._app.native.sendBrowserOpen(isAbsolute ? page : document.location.origin + page);
      } else {
        window.open(page, '_blank');
      }
    };

    if (tabBehavior === TabNavigationType.INTERNAL_SAME_EXTERNAL_NEW) {
      if (isInternal) {
        navigateInternalToSameTab();
      } else {
        window.open(page, '_blank', 'noopener');
      }
    } else if (tabBehavior === TabNavigationType.SAME) {
      if (isInternal) {
        navigateInternalToSameTab();
      } else {
        this._openExternalPage(page);
      }
    } else if (tabBehavior === TabNavigationType.NEW) {
      if (isInternal) {
        navigateInternalToNewTab();
      } else {
        window.open(page, '_blank', 'noopener');
      }
    }
  }

  public navigateToCategory(slug: string): void {
    this.navigateTo(this.getPathNavigateToCategory(slug, false));
  }

  public getPathNavigateToCategory(slug: string, includeLangPrefix = true): string {
    const langPrefix = includeLangPrefix ? `/${window.$app.appConfig.lang}` : '';
    return `${langPrefix}/${MainRoute.CATEGORY}/${slug}`;
  }

  public getPathNavigateToProvider(slug: string, includeLangPrefix = true): string {
    const langPrefix = includeLangPrefix ? `/${window.$app.appConfig.lang}` : '';
    return `${langPrefix}/${MainRoute.PROVIDER}/${slug}`;
  }

  public navigateToGame(playForReal: boolean, gameId: string) {
    window.$app.activationFlowModalService.setCurrentGameId(gameId);
    const gameMode = playForReal ? 'real' : 'demo';
    const isCompliant = window.$app.activationFlow.isActionEnabled(ComplianceActionTypes.GAMEPLAY);

    if (!isCompliant) {
      window.$app.activationFlowModalService.toggleAllMinification(false);
      window.$app.activationFlowModalService.requestUiUpdate();
      window.$app.logger.log('navigate to game not allowed due to compliance reasons:', gameId, '• mode:', gameMode);
      this._trackGameOpenFailed(gameId);
      return;
    }

    window.$app.logger.log('navigate to game:', gameId, '• mode:', gameMode);
    this._router.navigate(`/${MainRoute.GAME}/${gameMode}/${gameId}`);
  }

  public getPathNavigateToGameInfo(slug: string): string {
    return `/${window.$app.appConfig.lang}/${this._gameInfoSlug}/${slug}`;
  }

  public navigateToGameInfo(slug: string): void {
    window.$app.logger.log(`navigate to game-info: '${slug}'`);
    this._router.navigate(`/${this._gameInfoSlug}/${slug}`);
  }

  public navigateToTag(slug: string): void {
    window.$app.logger.log(`navigate to tag: '${slug}'`);
    this._router.navigate(this.getPathNavigateToTag(slug, false));
  }

  public getPathNavigateToTag(slug: string, includeLangPrefix = true): string {
    const langPrefix = includeLangPrefix ? `/${window.$app.appConfig.lang}` : '';
    return `${langPrefix}/tag/${slug}`;
  }

  public navigateToProviders(): void {
    this.navigateTo(this.getPathNavigateToProviders(false));
  }

  public navigateToProvider(slug: string): void {
    this.navigateTo(this.getPathNavigateToProvider(slug, false));
  }

  public getPathNavigateToProviders(includeLangPrefix = true): string {
    const langPrefix = includeLangPrefix ? `/${window.$app.appConfig.lang}` : '';
    return `${langPrefix}/${MainRoute.PROVIDER}`;
  }

  public navigateToAccount(): void {
    this.navigateTo(MainRoute.ACCOUNT);
  }

  public navigateToBonuses(id?: string): void {
    if (window.$app.appSettings.isSHCasino) {
      this.navigateToPromotions();
      return;
    }
    if (id) {
      this.navigateToIncentiveReward(id);
      return;
    }
    this.navigateToIncentive();
  }

  public getPathNavigateToBonuses(includeLangPrefix = true): string {
    if (window.$app.appSettings.isSHCasino) {
      return this.getPathNavigateToPromotions(includeLangPrefix);
    }
    const langPrefix = includeLangPrefix ? `/${window.$app.appConfig.lang}` : '';
    return `${langPrefix}/${MainRoute.INCENTIVE}`;
  }

  public navigateToPromotions(): void {
    this.navigateTo(
      this.getPathNavigateToPromotions(false),
      TabNavigationType.INTERNAL_SAME_EXTERNAL_NEW,
      ComplianceActionTypes.INCENTIVE,
    );
  }

  public getPathNavigateToPromotions(includeLangPrefix = true): string {
    const langPrefix = includeLangPrefix ? `/${window.$app.appConfig.lang}` : '';
    return `${langPrefix}/${this._promotionsSlug}`;
  }

  public getPathNavigateToSingleOffer(id: string): string {
    return `/${window.$app.appConfig.lang}/${this._promotionsSlug}/${this._promotionSlug}/${id}`;
  }

  public navigateToLimits(): void {
    this.navigateTo(`/${MainRoute.LIMITS}`);
  }

  public navigateToSearch(): void {
    this.navigateTo(this.getPathNavigateToSearch(false));
  }

  public getPathNavigateToSearch(includeLangPrefix = true): string {
    const langPrefix = includeLangPrefix ? `/${window.$app.appConfig.lang}` : '';
    return `${langPrefix}/${MainRoute.SEARCH}`;
  }

  public navigateToHome(): void {
    window.$app.logger.log('navigate to home');
    this._router.navigate(this.getPathNavigateToHome(false));
  }

  /**
   * Navigate to origin
   + Using navigate() of the router adds a trailing slash after the language prefix.
   * We don't want that if the page reloads.
   */
  public navigateToOrigin(): void {
    window.$app.logger.log('navigate to origin');
    this.pushState(`${document.location.origin}/${window.$app.appConfig.lang}`);
  }

  public navigateToGamification(): void {
    window.$app.logger.log('navigate to gamification');
    this._router.navigate(MainRoute.SHOW_GAMIFICATION_WIDGET);
  }

  public navigateToTaC(): void {
    window.$app.logger.log('navigate to T&C');
    this._router.navigate(MainRoute.TAC);
  }

  public getPathNavigateToHome(includeLangPrefix = true): string {
    return includeLangPrefix ? `/${window.$app.appConfig.lang}` : MainRoute.HOME;
  }

  public navigateToLogout(reason: LogOutReason, reload = false): void {
    const currentPath = window.location.pathname;
    const isAlreadyOnLogout = currentPath.includes(`/${MainRoute.LOGOUT}/`);
    if (isAlreadyOnLogout) {
      const currentReason = currentPath.split('/').pop();
      const intentionalReasons: string[] = [LogOutReason.PANIC, LogOutReason.OASIS];
      if (currentReason && intentionalReasons.includes(currentReason) && currentReason !== reason) {
        return;
      }
    }

    this._router.navigate(`${MainRoute.LOGOUT}/${reason}`);
    if (reload) {
      paintTick().then(() => {
        this.reloadPage();
      });
    }
  }

  public navigateToNotFound(): void {
    this._notFoundHandler();
  }

  public navigateToIncentive(): void {
    this.navigateTo(
      `${MainRoute.INCENTIVE}`,
      TabNavigationType.INTERNAL_SAME_EXTERNAL_NEW,
      ComplianceActionTypes.INCENTIVE,
    );
  }

  public navigateToIncentiveReward(id: string): void {
    this.navigateTo(
      `${MainRoute.INCENTIVE}/${MainRoute.INCENTIVE_REWARD}/${id}`,
      TabNavigationType.INTERNAL_SAME_EXTERNAL_NEW,
      ComplianceActionTypes.INCENTIVE,
    );
  }

  public navigateToGameHistory(): void {
    this._router.navigate(`${MainRoute.ACCOUNT}/${MainRoute.GAME_HISTORY}`);
  }

  public navigateToPaymentsHistory(): void {
    this._router.navigate(`${MainRoute.ACCOUNT}/${MainRoute.PAYMENTS_HISTORY}`);
  }

  public navigateToThemes(): void {
    this._router.navigate(`${MainRoute.ACCOUNT}/${MainRoute.THEMES}`);
  }

  /**
   * Resolves a shortcut link by finding the corresponding target in the _shortcuts array.
   *
   * @param path - The shortcut link to resolve.
   * @returns The resolved path or the original path if it cannot be resolved.
   */
  public resolveShortcutLink(path: string): string {
    const key = path.replace(document.location.href, '').replace(this._shortcutPrefix, '');
    const target = this._shortcuts.find((shortcut) => shortcut.key === key);

    if (target === undefined) {
      // Unknown => return shortcut link as is
      window.$app.logger.warn('Unknown shortcut link', path);
      return path;
    }

    if (target.action !== null) {
      // Has action and cannot be replaced => return shortcut link as is
      window.$app.logger.log('Shortcut link has action and cannot be replaced');
      return path;
    }

    return target.path ?? path;
  }

  public navigateToShortcutLink(path: string, sameTab = true): void {
    const key = path.replace(document.location.href, '').replace(this._shortcutPrefix, '');
    const target = this._shortcuts.find((_) => _.key === key);
    if (target === undefined) {
      window.$app.logger.warn('unknown shortcut link', path);
      return;
    }
    if (target.path !== null) {
      window.$app.logger.log(`shortcut link '${path}' target:`, target);
      this.navigateTo(target.path, sameTab ? TabNavigationType.INTERNAL_SAME_EXTERNAL_NEW : TabNavigationType.NEW);
    } else if (target.action !== null) {
      window.$app.logger.log('calling shortcut link action');
      target.action();
    }
  }

  public navigateToClubThousands(): void {
    this._router.navigate(this.getPathNavigateToClubThousands(false));
  }

  public getPathNavigateToClubThousands(includeLangPrefix = true): string {
    const langPrefix = includeLangPrefix ? `/${window.$app.appConfig.lang}` : '';
    return `${langPrefix}/${MainRoute.CLUB1000}`;
  }

  public isHomeRoute(): boolean {
    return this._router.root === this.currentLocation().route.name;
  }

  public isGameInfoRoute(): boolean {
    return this._gameInfoSlug === this.currentLocation().route.name.split('/')[0];
  }

  public isChallengesRoute(): boolean {
    return MainRoute.SHOW_GAMIFICATION_WIDGET === this.currentLocation().route.name.split('/')[0];
  }

  public getRouteName(): string {
    return this.currentLocation().route.name;
  }

  /**
   * Navigate to a page using replaceState instead of pushState.
   * The route handler still fires (before/after hooks, rendering), but no new
   * history entry is created. Use for tab switches where back should skip over.
   *
   * Only for internal relative paths where compliance gating and external URL
   * handling are not applicable (e.g. subnav tab switches within a view).
   */
  public navigateReplace(page: string): void {
    window.$app.logger.log(`navigate (replace) to '${page}'`);
    this._router.navigate(page, { historyAPIMethod: 'replaceState' });
  }

  /**
   * Update URL and browser history
   * Add new page to the browser history without navigating and hard-reloading the page.
   */
  public pushState(url: string): void {
    window.$app.logger.log('push state', url);
    if (url === this.currentLocation().url) {
      // Same url => skip
      window.$app.logger.log('push state skipped (same url)');
      return;
    }
    history.pushState({}, '', url);
  }

  public reloadPage(): void {
    document.location.reload();
  }

  public scrollToTop() {
    if (window.$app.appConfig.iOS26FixEnabled) {
      document.body.scrollTo({
        top: -1,
      });
      return;
    }

    window.scrollTo({
      top: window.$app.device.isIos ? -1 : 0,
    });
  }

  public setMaintenance(tf: boolean) {
    if (tf) {
      this._router.navigate(MainRoute.MAINTENANCE);
      return;
    }

    if (this.currentLocation().route.name === MainRoute.MAINTENANCE) {
      this.navigateBack();
    }
  }

  public changeLanguage(languageCode: string) {
    window.$app.logger.log(`change route to ${languageCode}`);
  }

  public async renderView(view: TemplateResult, pageTemplate: PageTemplate) {
    // Page requires user authentication
    if (pageTemplate.options?.requireUserAuthentication) {
      if (this._loggedIn === undefined) {
        await this._loginStatus();
      }

      // Show login-required-page if not logged in
      if (!this._loggedIn) {
        window.$app.logger.log('page requires authentication => show login-required-page');
        // biome-ignore lint/style/noParameterAssign: Necessary to modify the view for login-required-page
        view = html`<login-required-page></login-required-page>`;

        // A BLANK route is built for a full-viewport view that brings its own
        // chrome (the cashier). The login-required page is not that view — it
        // needs our header and nav to be navigable — so frame it like the
        // dedicated LOGIN_REQUIRED route. Swapping the template rather than
        // redirecting keeps the URL, so `gotoLogin()` returns the user here
        // after login. BASE routes keep their own template.
        if (pageTemplate.template === TemplateType.BLANK) {
          // biome-ignore lint/style/noParameterAssign: Necessary to reframe the substituted login-required-page
          pageTemplate = LOGIN_REQUIRED_PAGE_TEMPLATE;
        }
      }
    }

    // Render in desktop sidebar when logged in and allowDesktopSlideIn is set
    if (this._isDesktop && this._loggedIn === undefined) {
      await this._loginStatus();
    }
    if (this._isDesktop && this._loggedIn && pageTemplate.options?.allowDesktopSlideIn) {
      // Only <base-page> renders the slide-in. With no root (desktop deep link,
      // IGM-482) or another root (BLANK cashier, 404) render the lobby underneath.
      // Closing the slide-in then returns home: the page before the slide-in is
      // not recoverable here.
      if (!this._root.has() || this._root.get() !== this._basePageRoot) {
        window.$app.logger.log('slide-in without base-page root => render lobby backdrop');
        this._renderLobby();
        // must follow _renderLobby(), which resets _preSlideInPath
        this._preSlideInPath = MainRoute.HOME;
      }
      this._renderViewInDesktopSidebar(view, pageTemplate);
      return;
    }

    // Render as page (mobile, logged out, SEO)
    this._renderViewAsPage(view, pageTemplate);
  }

  /**
   * Revert route to page before slide-in was opened.
   * Beware that the user might have navigated to different routes while the slide-in was open.
   */
  public closeSlideIn() {
    this._clearDesktopSlideIn();
    this.navigateTo(this._preSlideInPath ?? MainRoute.HOME);
    this._preSlideInPath = undefined;
    this._isSidebarOpen = false;
    dispatchCustomEvent(window, SidebarEventName);
  }

  public getIsSidebarOpen() {
    return this._isSidebarOpen;
  }

  public isGameRoute(): boolean {
    return MainRoute.GAME === this.currentLocation().route.name.split('/')[0];
  }

  public getBackButtonLabel() {
    return this._previousLocation === null || ['', '/'].includes(this._previousLocation)
      ? window.$app.$t.get(I18n.base.home)
      : window.$app.$t.get(I18n.base.back);
  }

  private _renderViewAsPage(view: TemplateResult, pageTemplate: PageTemplate) {
    const { template, options } = pageTemplate;
    // as sidebar is closing, we need to reset this value
    this._preSlideInPath = undefined;
    this._isSidebarOpen = false;
    dispatchCustomEvent(window, SidebarEventName);

    if (template === TemplateType.BASE) {
      window.$app.logger.log('render using BASE template…', options);
      this._basePageRoot = html`
        <base-page
          class="${(options?.showMobileBottomBar ?? true) ? 'mobile-bar-shown' : ''}"
          .headerType=${options?.headerType ?? BasePageHeaderType.DEFAULT}
          .isMobileHeaderShown=${options?.isMobileHeaderShown ?? true}
          .showBottomBar=${options?.showMobileBottomBar ?? true}
          .isFooterShown=${options?.isFooterShown ?? true}
          .isFullScreen=${options?.isFullScreen ?? false}
        >
          ${view}
        </base-page>
      `;
      this._root.set(this._basePageRoot);
      return;
    }

    if (template === TemplateType.BLANK) {
      window.$app.logger.log('render using BLANK template…', options);
      this._root.set(html`<blank-page>${view}</blank-page>`);
      return;
    }

    if (template === TemplateType.GAMES_LIST) {
      window.$app.logger.log('render using GAMES_LIST template…');
      this._root.set(html`<cat-filter-page>${view}</cat-filter-page>`);
      return;
    }

    window.$app.logger.warn('unknown page template:', template);
  }

  private _renderViewInDesktopSidebar(view: TemplateResult, pageTemplate: PageTemplate) {
    this._preSlideInPath = this._preSlideInPath ?? this._previousLocation ?? undefined;
    this._isSidebarOpen = true;
    const payload: ShowSidebarEvent = {
      type: SidebarEventType.SHOW,
      view,
      pageTemplate,
    };
    window.$app.logger.log('render in sidebar => dispatch event with payload:', payload);
    requestAnimationFrame(() => {
      dispatchCustomEvent(window, SidebarEventName, payload);
    });
  }

  private _renderSearchPage() {
    this.renderView(html`<search-page></search-page>`, {
      template: TemplateType.BASE,
      options: {
        allowDesktopSlideIn: true,
        isFooterShown: true,
      },
    });
  }

  private _renderGameInfoPage(slug: string) {
    this.renderView(html`<game-info-page .slug=${slug}></game-info-page>`, {
      template: TemplateType.BASE,
      options: {
        allowDesktopSlideIn: true,
        headerType: BasePageHeaderType.WITH_ICONS,
      },
    });
  }

  private _renderLobby() {
    // SH Casino
    if (window.$app.appSettings.isSHCasino) {
      this._renderViewAsPage(html`<cat-filter-page><lobby-sh-page></lobby-sh-page></cat-filter-page>`, {
        template: TemplateType.BASE,
        options: {
          headerType: BasePageHeaderType.FULL,
        },
      });
      return;
    }
    // Games
    this._renderViewAsPage(html`<cat-filter-page><lobby-page></lobby-page></cat-filter-page>`, {
      template: TemplateType.BASE,
      options: {
        headerType: BasePageHeaderType.FULL,
      },
    });
  }

  private _renderRouteSpinner() {
    this._root.set(
      html`<div style="display:grid;height:100lvh;align-items:center;justify-content:center;"><ui-spinner></ui-spinner></div>`,
    );
  }

  private _notFoundHandler() {
    // Show spinner if routes are not finalized yet. Will be resolved later.
    if (!this._routesFinalized) {
      this._renderRouteSpinner();
      return;
    }

    window.$app.logger.log('page not found');
    window.$app.track.pageNotFound('shown', this._router?.getCurrentLocation?.().route.path.toString());
    this._root.set(html`<error-page-404></error-page-404>`);
  }

  private async _resolveGuardedRoute(guard: RouteGuard, match: Match | undefined, handler: (match?: Match) => void) {
    const navigationId = this._navigationId;
    // Cold load: nothing is on screen until the guard settles.
    if (!this._root.has()) this._renderRouteSpinner();

    const outcome = await guard(match).catch((err: unknown): RouteGuardOutcome => {
      window.$app.logger.error(`route guard of '${match?.url}' failed; denying`, err);
      return { type: RouteGuardOutcomeType.Deny };
    });
    if (navigationId !== this._navigationId) {
      window.$app.logger.log(`route guard of '${match?.url}' settled after navigation; ignored`);
      return;
    }

    if (outcome.type === RouteGuardOutcomeType.Allow) {
      handler(match);
      return;
    }
    if (outcome.type === RouteGuardOutcomeType.Redirect) {
      window.$app.logger.log(`route guard redirects '${match?.url}' to '${outcome.to}'`);
      this.navigateReplace(outcome.to);
      outcome.onRedirected?.();
      return;
    }
    window.$app.logger.log(`route guard denies '${match?.url}'`);
    this.navigateReplace(this._pathBeforeCurrentRoute());
    outcome.onDenied?.();
  }

  /** The in-app page before the current route, or home when the route was the first one. */
  private _pathBeforeCurrentRoute(): string {
    const previous = this._previousLocation;
    if (previous === null || ['', '/'].includes(previous)) return this.getPathNavigateToHome(false);
    return previous.startsWith('/') ? previous : `/${previous}`;
  }

  private _openExternalPage(page: string) {
    window.$app.logger.log('open external link:', page);
    this._root.unload();
    window.open(page, '_self', 'noopener');
  }

  /**
   * Releases decoded image data from GPU memory before a heavy page transition.
   * WebKit keeps decoded images in the GPU process even after their DOM nodes are
   * removed. Clearing the src attribute forces the image buffers to be released (IGM-3644).
   */
  private _releaseImageMemory() {
    const clearImages = (root: Document | ShadowRoot) => {
      for (const img of root.querySelectorAll('img[src]')) {
        (img as HTMLImageElement).removeAttribute('src');
        (img as HTMLImageElement).removeAttribute('srcset');
      }
      // Traverse into shadow DOMs of custom elements
      for (const el of root.querySelectorAll('*')) {
        if (el.shadowRoot) {
          clearImages(el.shadowRoot);
        }
      }
    };
    clearImages(document);
  }

  private _clearDesktopSlideIn() {
    const payload: ClearSidebarEvent = {
      type: SidebarEventType.CLEAR,
    };
    dispatchCustomEvent(window, SidebarEventName, payload);
  }

  /**
   * Subscribe to login status changes. Use the final login status to determine if the user is logged in or not.
   */
  private _subscribeLoginStatus() {
    window.$app.loginStore.subscribe((loginObj) => {
      // Set login status when known as logged in or logged out. Ignore fetching state.
      if (loginObj.loginStatus === LoginStatus.LOGGED_IN || loginObj.loginStatus === LoginStatus.LOGGED_OUT) {
        this._loggedIn = loginObj.loginStatus === LoginStatus.LOGGED_IN;
      }
    });
  }

  /**
   * Await final login status. Either logged in or logged out.
   */
  private async _loginStatus(): Promise<typeof LoginStatus.LOGGED_IN | typeof LoginStatus.LOGGED_OUT> {
    return await window.$app.login.getFinalLoginStatus();
  }

  /**
   * Remove trailing slash from path and update browser history.
   */
  private _removeTrailingSlash() {
    paintTick().then(() => {
      if (document.location.pathname.endsWith('/')) {
        window.history.replaceState(history.state, '', document.location.pathname.slice(0, -1));
      }
    });
  }

  /**
   * Manages history for forwarder routes so they never create a reachable entry.
   * - SPA navigate(): intercepts Navigo's deferred pushState, converts to replaceState.
   * - Back/forward arrival: cancels route, replaces entry, redirects to lobby.
   * @returns true if the route was cancelled (caller should return early).
   */
  private _handleForwarderRoute(done: (shouldContinue: false | Match) => void): boolean {
    if (this._isForwarderResolvedOnPageLoad()) {
      this._replaceCurrentWithPrevious();
      done(false);
      this.navigateToHome();
      return true;
    }
    this._interceptNextPushState();
    return false;
  }

  /**
   * Checks whether a route name belongs to the forwarder set.
   * Navigo prepends the root path (lang code) to route names — strip before matching.
   */
  private _isForwarderRoute(routeName: string): boolean {
    const stripped = routeName.startsWith(`${this._rootPath}/`)
      ? routeName.slice(this._rootPath.length + 1)
      : routeName;
    return this._forwarderRoutes.has(stripped);
  }

  /**
   * Monkey-patches history.pushState so the next call becomes a replaceState
   * targeting the previous SPA location. Navigo's updateBrowserURL middleware
   * calls pushState AFTER the route lifecycle — this intercept converts that
   * push into a no-op replace so forwarder routes never enter the history stack.
   */
  private _interceptNextPushState() {
    const originalPushState = history.pushState.bind(history);
    const previousUrl = this._buildPreviousUrl();

    history.pushState = (state: unknown, title: string, _url?: string | URL | null) => {
      history.pushState = originalPushState;
      history.replaceState(state, title, previousUrl);
    };

    // Restore original if no pushState fires (resolve() path).
    queueMicrotask(() => {
      if (history.pushState !== originalPushState) {
        history.pushState = originalPushState;
      }
    });
  }

  /**
   * Replaces the current history entry with the previous SPA location.
   * Used on the resolve() path (back-nav) where no pushState follows and the page
   * would unload before any deferred replacement fires.
   */
  private _replaceCurrentWithPrevious() {
    history.replaceState(history.state, '', this._buildPreviousUrl());
  }

  /**
   * Builds the full URL for the previous SPA location, falling back to the
   * language root when no meaningful previous location exists.
   */
  private _buildPreviousUrl(): string {
    const lang = window.$app.appConfig.lang;
    return this._previousLocation && this._previousLocation !== '/' && this._previousLocation !== ''
      ? `/${lang}${this._previousLocation}`
      : `/${lang}`;
  }

  /**
   * Detects back/forward navigation landing on a forwarder URL. Returns false for
   * SPA-internal navigations and fresh page loads (new tab, bookmark, direct URL).
   */
  private _isForwarderResolvedOnPageLoad(): boolean {
    if (this._hasCompletedFirstRoute) return false;
    const navEntries = performance.getEntriesByType('navigation');
    if (navEntries.length === 0) return false;
    return (navEntries[0] as PerformanceNavigationTiming).type === 'back_forward';
  }

  private _storeScrollPosition(page: string, pos = window.scrollY) {
    this._scrollPostions.set(page, pos);
  }

  private _cleanupScrollRestoration() {
    document.body.style.minHeight = 'auto';
  }

  private _initHooks() {
    this._router.hooks({
      before: this._beforeHook,
      after: this._afterHook,
      already: () => {
        paintTick().then(() => {
          this._removeTrailingSlash();
          // Same URL without hash => scroll to top, with hash => scroll to section
          if (window.location.hash) {
            dispatchCustomEvent(window, 'hashchange');
          } else {
            this.scrollToTop();
          }
        });
      },
    });
  }

  private _addNotFound() {
    this._router.notFound(this._notFoundHandler.bind(this));
  }

  private _lockTab() {
    window.$app.logger.log('locking tab');
    window.$app.track.inactiveTab('shown');
    const isDevMode = (import.meta as any).env.DEV;
    const path = isDevMode
      ? `/${window.$app.appConfig.lang}/tab-locked.html?lang=${window.$app.appConfig.lang}`
      : `/tab-locked.html?lang=${window.$app.appConfig.lang}`;
    this._navigateToStaticPage(path);
  }

  /**
   * Navigate to a static page and disable router.
   * Without disabling it, the router can get stuck in a loop.
   * @param path - The path to navigate to.
   */
  private _navigateToStaticPage(path: string): void {
    window.$app.logger.log('Navigate to static page:', path);
    window.$app.logger.warn('Router has been destroyed. No further route resolution.');
    // @ts-expect-error
    this._router = {};
    // Create a new navigate function that logs a warning to prevent Sentry logs.
    this._router.navigate = (navPath: string, options?: NavigateOptions) => {
      window.$app.logger.warn(
        `Router has been destroyed. Ignoring path: '${navPath}' and options: '${JSON.stringify(options)}'`,
      );
    };
    document.location.assign(path);
  }

  private _getSourceAndCleanUrlForTracking() {
    const url = new URL(window.location.href);
    const source = url.searchParams.get('source');

    if (source) {
      url.searchParams.delete('source');
      window.history.replaceState({}, document.title, url.pathname + url.search);
    }

    if (source === 'tab-locked') {
      window.$app.track.inactiveTab('useHere');
    }
  }

  private _setupEventBusListeners() {
    eventBus.on(ROUTER_EVENTS.NAVIGATE_TO, ({ page, tabBehavior, complianceAction }) => {
      this.navigateTo(page, tabBehavior, complianceAction);
    });

    eventBus.on(ROUTER_EVENTS.NAVIGATE_TO_GAME, ({ playForReal, gameId }) => {
      this.navigateToGame(playForReal, gameId);
    });
  }

  private _trackGameOpenFailed(gameId: string) {
    let errorMessage = '';
    const currentState = window.$app.activationFlow.getCurrentState();
    switch (currentState) {
      case UserStates.COOL_OFF:
        errorMessage = window.$app.$t.get(I18n.compliance.coolOffModal.message);
        break;
      case UserStates.REALITY_CHECK_REQUIRED:
        errorMessage = window.$app.$t.get(I18n.compliance.realityCheckModal.required.text);
        break;
      case UserStates.REALITY_CHECK:
        errorMessage = window.$app.$t.get(I18n.compliance.realityCheckModal.ongoing.text);
        break;
      case UserStates.ACTIVATION_COOL_OFF:
        errorMessage = window.$app.$t.get(I18n.compliance.activationCoolOffModal.title);
        break;
      case UserStates.ACTIVATION_FAILED:
        errorMessage = window.$app.$t.get(I18n.compliance.retryActivation.title);
    }
    window.$app.track.gameOpenFailed(gameId, errorMessage);
  }

  private get _promotionSlug(): string {
    return this._app.featureFlag.promotionSlug;
  }

  private get _promotionsSlug(): string {
    return this._app.featureFlag.promotionsSlug;
  }

  private get _gameInfoSlug(): string {
    return this._app.featureFlag.gameInfoSlug;
  }
}
